import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { RightPanelService } from 'src/app/core/services/right-panel.service';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { TicketNotesComponent } from '../ticket-notes.component';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { NoteService } from 'src/app/core/services/note.service';
import { AttachmentsModalComponent } from '../../../../shared/modal-windows/attachments-modal/attachments-modal.component';
import { NoteDataService } from 'src/app/core/services/note-data.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../../../../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { WindowRef } from '@sb-tools/typescript/services/window-ref.service';
import { TicketDetailsPanelService } from 'src/app/core/services/ticket-details-panel.service';
import { NoteGuiModel } from '@sb-tools/typescript/models/view/note-gui-model.model';
import Quill from 'quill';
import { Range } from 'quill/core/selection';
import { NoteDTO } from '../../../../core/models/dto/note.dto';
import { NoteUpdateDTO } from '../../../../core/models/dto/note-update.dto';
import { NoteDeletedResponseDTO } from '../../../../core/models/dto/note-deleted-response.dto';
import { DatePipe, NgClass } from '@angular/common';
import { UserAvatarComponent } from '../../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ReplyQuoteComponent } from '../../../../shared/components/reply-quote/reply-quote.component';
import { HighlightMentionComponent } from '../../../../shared/components/highlight-mention/highlight-mention.component';
import { FilePreviewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { NoteQuillEditorComponent } from '../../../../shared/components/note-quill-editor/note-quill-editor.component';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ClipboardModule } from 'ngx-clipboard';
import { MentionNotificationComponent } from './mention-notification/mention-notification.component';
import { NewNoteComponent } from '../../../../shared/components/new-note/new-note.component';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';
import { LastSubReplyPipe } from '../../../../shared/pipes/last-subReply.pipe';
import { TextForNotesExpanderPipe } from '../../../../shared/pipes/text-for-notes-expander.pipe';

@Component({
    selector: 'app-note-item',
    templateUrl: './note-item.component.html',
    styleUrls: ['./note-item.component.scss'],
    imports: [
        NgClass,
        UserAvatarComponent,
        TooltipModule,
        ReplyQuoteComponent,
        HighlightMentionComponent,
        FilePreviewComponent,
        NoteQuillEditorComponent,
        InlineSVGComponent,
        BsDropdownModule,
        ClipboardModule,
        MentionNotificationComponent,
        NewNoteComponent,
        DatePipe,
        TranslateModule,
        TimeAgoPipe,
        LastSubReplyPipe,
        TextForNotesExpanderPipe,
    ],
})
export class NoteItemComponent implements OnInit, OnDestroy {
    @Input() index: number;
    @Input() isSubNote?: boolean;
    @Input() note: NoteDTO;
    @Input() parentNote?: NoteDTO;

    @Output() handleDelete = new EventEmitter<NoteDeletedResponseDTO>();

    attachments = [];
    editableNote: NoteGuiModel = {
        text: '',
        html: '',
        attachments: [],
    };
    isEditableNote: boolean;
    isInvalidMessage = false;
    isSaving = false;
    noteLink: string;
    quillEditor: Quill;
    quillRange: Range;
    rightPanelTypeEnum = RightPanelType;

    private tempAttachments = [];
    private unsubscriber = new Subject<void>();

    constructor(
        public noteService: NoteService,
        public rightPanelService: RightPanelService,
        public ticketDetailsPanelService: TicketDetailsPanelService,
        public commonDataService: CommonDataService,
        @Inject(TicketNotesComponent) public parent: TicketNotesComponent,
        private replyTemplateManagerService: ReplyTemplateManagerService,
        private bsModalService: BsModalService,
        private noteDataService: NoteDataService,
        private translateService: TranslateService,
        private windowRef: WindowRef,
    ) {}

    ngOnInit(): void {
        this.tempAttachments = structuredClone(this.note.attachments);
        this.isEditableNote =
            this.commonDataService.user.id === this.note.author.id || this.commonDataService.user.isAdmin;

        this.noteLink =
            this.windowRef.nativeWindow.location.origin +
            '/' +
            this.commonDataService.community +
            '/tickets?ticketId=' +
            this.ticketDetailsPanelService.currentTicketId +
            '&noteId=' +
            this.note.id;
    }

    cancelEditNote(): void {
        this.note.attachments = this.tempAttachments;
        this.note.isEditing = false;
    }

    doneEditNote(): void {
        if (this.isSaving) {
            return;
        }

        this.isSaving = true;
        if (this.editableNote.html) {
            this.editableNote.html = this.editableNote.html.replaceAll('<div></div>', '<div><br></div>');
        }

        const updatedNote: NoteUpdateDTO = {
            replyId: this.note.reply ? this.note.reply.id : null,
            parentNoteId: this.note.parentNoteId,
            message: {
                text: this.editableNote.text,
                html: this.editableNote.html,
            },
            mentionedUserIds: this.noteService
                .getMentorIds(this.editableNote.html)
                .concat(this.noteService.getMentionedIds('user', this.editableNote.html)),
            mentionedUserGroupIds: this.noteService.getMentionedIds('group', this.editableNote.html) as number[],
            attachments: this.attachments,
            ticketId: this.parent.ticket.id,
        };

        this.noteDataService
            .update(this.parent.ticket.id, this.note.id, updatedNote)
            .pipe(
                finalize(() => {
                    this.isSaving = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data) => {
                data.subNotes = this.note.subNotes;
                Object.assign(this.note, data);
                this.tempAttachments = structuredClone(this.note.attachments);
                this.note.isEditing = false;
            });
    }

    handleDeleteSubNote(noteDeletedResponse: NoteDeletedResponseDTO): void {
        this.handleDelete.emit(noteDeletedResponse);
    }

    onSubNoteAdd(note: NoteDTO, parentNote: NoteDTO): void {
        parentNote.isSubNoteEditorShown = false;
        if (
            !parentNote.subNotes.find((subNote) => {
                return subNote.id === note.id;
            })
        ) {
            parentNote.subNotes.push(note);
        }
    }

    openAttachmentsModal(): void {
        const initialState = {
            newAttachments: this.attachments,
            existedAttachmentsLength: this.attachments.length,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    openDeleteModal(): void {
        const initialState = {
            title: this.translateService.instant('Are you sure you want to delete this note?'),
            confirmText: this.translateService.instant('Delete'),
            declineText: this.translateService.instant('Cancel'),
            confirmCallback: () => {
                return this.deleteNote(this.note);
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    showEditView(): void {
        this.note.isEditing = true;
        this.attachments = this.note.attachments;
        this.editableNote.text = this.note.message.text;
        this.editableNote.html = this.note.message.html;
        if (!this.editableNote.html) {
            this.editableNote.html = this.note.message.text;
        }

        setTimeout(() => {
            this.quillEditor.setSelection(this.note.message.text.length);
            this.quillEditor.focus();
        }, 100);
    }

    toggleSubNoteEditor(isShow): void {
        const note = this.isSubNote ? this.parentNote : this.note;
        note.isSubNoteEditorShown = isShow;

        if (isShow) {
            note.newSubNote = {
                text: '',
                html: '',
                attachments: [],
            };

            setTimeout(() => {
                const element = this.windowRef.nativeWindow.document.querySelector<HTMLElement>(
                    `#new-note-${note.id} .ql-editor`,
                );
                element?.focus();
            });
        }
    }

    updateQuillSelection(): void {
        this.quillRange = this.replyTemplateManagerService.updateRange(this.quillEditor);
    }

    private deleteNote(note: NoteDTO): void {
        this.noteDataService
            .delete(this.parent.ticket.id, note.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((deletedNote) => {
                this.handleDelete.emit(deletedNote);
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
